<template>
  <Teleport to="body">
    <SlotPanel
      v-if="hasUpdates || state === STATE.SAVED"
      class="flex flex-col gap-2 z-50 justify-center items-baseline w-full bg-surface-primary py-4 sticky bottom-0 border-t border-linear-subtle text-fg-primary sm:gap-8 sm:h-16 sm:flex-row"
    >
      <template v-if="state === STATE.INITIAL">
        <UpdateIndicator id="message" status="updated">
          <span class="text-fg-primary">You have unsaved changes.</span>
        </UpdateIndicator>
        <SlotButton id="discard-all-button" alt @click="setState(STATE.CONFIRM_DISCARD)">
          Discard All
        </SlotButton>
        <SlotButton id="save-changes-button" @click="saveUpdates" :disabled="!validToSubmit">
          Save Changes
        </SlotButton>
      </template>
      <template v-else-if="state === STATE.CONFIRM_DISCARD">
        <span id="confirm-discard-message">Are you sure you want to discard all changes?</span>
        <SlotButton id="confirm-discard-button" altWarning @click="discardUpdates">
          Discard
        </SlotButton>
        <SlotButton id="save-button" @click="saveUpdates"> Save </SlotButton>
      </template>
      <template v-else-if="state === STATE.SAVING">
        <span id="saving-message"> Saving changes... </span>
      </template>
      <template v-else-if="state === STATE.SAVED">
        <span id="saved-message"> Changes saved! </span>
      </template>
      <template v-else-if="state === STATE.ERROR">
        <span id="error-message" class="text-alerts-warning">
          Error saving changes. Try again.
        </span>
        <SlotButton id="error-discard-button" altWarning @click="discardUpdates">
          Discard
        </SlotButton>
        <SlotButton id="error-save-button" @click="saveUpdates"> Save Again </SlotButton>
      </template>
    </SlotPanel>
  </Teleport>
  <Toast />
</template>

<script setup lang="ts">
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import { ref, toRefs, watch } from 'vue'

import SlotButton from '@/common/components/atoms/SlotButton.vue'
import UpdateIndicator from '@/common/components/atoms/UpdateIndicator.vue'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import { usePostHog } from '@/common/composables/usePostHog'

type State = (typeof STATE)[keyof typeof STATE]

const STATE = {
  INITIAL: 'initial',
  CONFIRM_DISCARD: 'confirm-discard',
  SAVING: 'saving',
  SAVED: 'saved',
  ERROR: 'error',
}
const state = ref<State>(STATE.INITIAL)

function setState(newState: State) {
  state.value = newState
}

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: 'discardUpdates'): void
}>()
const props = withDefaults(
  defineProps<{
    hasUpdates: boolean
    saveUpdates: () => Promise<void>
    validToSubmit?: boolean
    module?: string
  }>(),
  {
    validToSubmit: true,
    module: '',
  },
)
const { hasUpdates, validToSubmit } = toRefs(props)

function discardUpdates() {
  emit('discardUpdates')
  setState(STATE.INITIAL)
}

const toast = useToast()
const posthog = usePostHog()
const AUTO_DISMISS_TIME_MS = 4000
async function saveUpdates() {
  if (!validToSubmit.value) return
  try {
    setState(STATE.SAVING)
    await props.saveUpdates()
    posthog.capture('saved_changes', {
      timestamp: Date.now(),
      module: props.module,
      component: 'UnsavedChangesBottomBar',
    })
    if (posthog.isFeatureEnabled('unsaved_changes_bar_immediate_save')) {
      toast.add({
        severity: 'success',
        summary: 'Success!',
        detail: 'Your changes have been saved.',
        life: AUTO_DISMISS_TIME_MS,
      })
      setState(STATE.INITIAL)
    } else {
      setState(STATE.SAVED)
      setTimeout(() => {
        setState(STATE.INITIAL)
      }, AUTO_DISMISS_TIME_MS)
    }
  } catch (error) {
    setState(STATE.ERROR)
  }
}

watch(hasUpdates, hasUpdates => {
  if (!hasUpdates && state.value !== STATE.SAVED) {
    setState(STATE.INITIAL)
  }
})
</script>
